"use client";

import { artistLinkRel } from "@/lib/seo-enrichment";
import { formatEventHeadline } from "@/lib/event-title";

import { useState, useEffect } from "react";
import { useTranslations, useLocale } from "next-intl";
import { Link, useRouter } from "@/i18n/navigation";
import Image from "next/image";
import { MapPin, ExternalLink, Calendar, X, Pencil } from "lucide-react";
import { Event, CityConfig, RelatedShowing, Attraction } from "@/lib/types";
import { ArtistExternalLinks } from "../artist-external-links";
import {
  formatDate,
  formatTime,
  formatPrice,
  getEventImage,
  getEventGenres,
} from "@/lib/utils";
import { useSpotifyPlayer } from "../spotify/spotify-player-context";
import { useRelatedJukebox } from "../spotify/use-related-jukebox";
import { useSetSpotifyCitySlug } from "../spotify/use-set-city-slug";
import { AuthProvider, useAuth } from "../auth/auth-context";
import { SubscribeButton } from "../subscribe-button";
import { ArtistEditModal } from "../admin/artist-edit-modal";
import { EventAdminModal } from "../admin/event-admin-modal";
import { UserMenu } from "../auth/user-menu";
import { LanguageSwitcher } from "../language-switcher";
import { SearchButton } from "../search/search-button";
import { ReportButton } from "../report/report-button";
import { User } from "@supabase/supabase-js";
import dynamic from "next/dynamic";
import { PlayIcon } from "@/components/spotify/play-icon";
import { useDiscoveryWebMcp } from "../discovery/use-discovery-webmcp";
import { isDiscoveryArtist, isDiscoveryEvent } from "@/lib/discovery/policy";

const loadJukebox = () => import("./genre-jukebox-modal");
const GenreJukeboxModal = dynamic(() => loadJukebox().then((module) => module.GenreJukeboxModal), { ssr: false });

interface FestivalLink {
  name: string;
  slug: string;
  citySlug: string;
}

interface EventPageClientProps {
  event: Event;
  relatedShowings: RelatedShowing[];
  city: CityConfig;
  blockedSpotifyIds: string[];
  discoveryBlockedSpotifyIds?: string[];
  initialUser?: User | null;
  festival?: FestivalLink;
}

function SpotifyPlayButton({
  spotifyId,
  artistName,
  event,
  attractionId,
}: {
  spotifyId: string;
  artistName: string;
  event: Event;
  attractionId: number;
}) {
  const { play, artistId: activeArtistId } = useSpotifyPlayer();
  const isPlaying = spotifyId === activeArtistId;

  return (
    <button
      onClick={() => play(spotifyId, artistName, { event, artistId: attractionId })}
      title={`Preview ${artistName}`}
      className={`flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-full transition-all ${
        isPlaying
          ? "bg-[#1DB954]/20 text-[#1DB954] shadow-[0_0_8px_rgba(29,185,84,0.3)]"
          : "text-neutral-400 hover:text-[#1DB954] hover:bg-neutral-700"
      }`}
    >
      <PlayIcon className="h-4 w-4" />
    </button>
  );
}

function EventArtistRow({
  attraction: a,
  blockedSpotifyIds,
  event,
}: {
  attraction: Attraction;
  blockedSpotifyIds: string[];
  event: Event;
}) {
  const { isAdmin } = useAuth();
  const [showEditModal, setShowEditModal] = useState(false);

  const hasSpotify = a.spotify_id && !blockedSpotifyIds.includes(a.spotify_id);
  const hasLinks = a.external_links && Object.keys(a.external_links).length > 0;

  return (
    <div className="py-3 first:pt-0 last:pb-0">
      <div className={`flex gap-2.5 ${hasLinks ? "items-start" : "items-center"}`}>
        <Link
          href={`/artist/${a.slug}`}
          rel={artistLinkRel(a)} prefetch={false}
          className={`relative h-11 w-11 shrink-0 overflow-hidden rounded-full border border-neutral-700 ${hasLinks ? "mt-0.5" : ""}`}
        >
          {a.image_url ? (
            <Image
              src={a.image_url}
              alt={a.name}
              fill
              className="object-cover"
              sizes="44px"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-neutral-800 font-mono text-xs text-neutral-400">
              {a.name.charAt(0).toUpperCase()}
            </div>
          )}
        </Link>
        <div className={`min-w-0 flex-1 ${hasLinks ? "pt-1" : ""}`}>
          <Link
            href={`/artist/${a.slug}`}
            rel={artistLinkRel(a)} prefetch={false}
            className="block truncate font-mono text-sm text-neutral-200 transition-colors hover:text-acid"
          >
            {a.name}
          </Link>
          {hasLinks && (
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              <ArtistExternalLinks links={a.external_links} size="sm" />
            </div>
          )}
        </div>
        {hasSpotify && (
          <SpotifyPlayButton spotifyId={a.spotify_id as string} artistName={a.name} event={event} attractionId={a.id} />
        )}
        <SubscribeButton
          type="artist"
          targetId={a.id}
          targetName={a.name}
          targetImage={a.image_url}
          size="sm"
        />
        {isAdmin && (
          <>
            <button
              onClick={() => setShowEditModal(true)}
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded text-neutral-600 transition-all hover:text-acid cursor-pointer"
              title="Edit artist"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                <path d="m15 5 4 4" />
              </svg>
            </button>
            {showEditModal && (
              <ArtistEditModal
                artist={{
                  id: a.id,
                  name: a.name,
                  slug: a.slug,
                  genres: a.genres,
                  spotify_id: a.spotify_id,
                  image_url: a.image_url,
                }}
                onClose={() => setShowEditModal(false)}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

function AdminEventEditButton({ event }: { event: Event }) {
  const { isAdmin } = useAuth();
  const [editOpen, setEditOpen] = useState(false);
  if (!isAdmin) return null;
  return (
    <>
      <button
        onClick={() => setEditOpen(true)}
        className="flex h-8 w-8 items-center justify-center rounded-full border border-neutral-700 text-neutral-500 transition-colors hover:border-acid hover:text-acid"
        title="Edit event"
      >
        <Pencil className="h-3.5 w-3.5" />
      </button>
      {editOpen && (
        <EventAdminModal event={event} onClose={() => setEditOpen(false)} />
      )}
    </>
  );
}

export function EventPageClient({
  event,
  relatedShowings,
  city,
  blockedSpotifyIds,
  discoveryBlockedSpotifyIds,
  initialUser = null,
  festival,
}: EventPageClientProps) {
  const t = useTranslations("Events");
  const tJukebox = useTranslations("Jukebox");
  const tCommon = useTranslations("Common");
  const locale = useLocale();
  const router = useRouter();
  useSetSpotifyCitySlug(city.slug);
  const { artistId: spotifyActiveId, startPlayback } = useSpotifyPlayer();
  const relatedJukebox = useRelatedJukebox(city.slug, blockedSpotifyIds);
  const spotifyBarVisible = !!spotifyActiveId;

  const [genresExpanded, setGenresExpanded] = useState(false);
  const [hasHistory, setHasHistory] = useState(false);
  const imageUrl = getEventImage(event);
  const ticketUrl = event.url || event.venue?.url;
  const isCancelled = event.status === "cancelled";
  const allGenres = getEventGenres(event);
  // Same text as the <title>: leading acts, "@ venue".
  const headline = formatEventHeadline({
    name: event.name,
    venueName: event.venue.name,
    artistNames: event.attractions.map((a) => a.name),
  });

  const playableArtists = event.attractions.filter((artist) => artist.spotify_id && !blockedSpotifyIds.includes(artist.spotify_id));
  const playingArtist = playableArtists.find((artist) => artist.spotify_id === spotifyActiveId);
  const playerArtist = playingArtist ?? playableArtists[0];
  const playerTarget = { event, artistId: playerArtist?.id };
  const startPlayer = () => {
    if (!playerArtist?.spotify_id) return;
    startPlayback(playerArtist.spotify_id, playerArtist.name, playerTarget);
    relatedJukebox.openJukebox(playerTarget);
  };
  const discoveryBlocked = discoveryBlockedSpotifyIds ?? blockedSpotifyIds;
  const listeningArtist = event.attractions.find((artist) => isDiscoveryArtist(artist, discoveryBlocked));
  const discoveryEligible = !festival && isDiscoveryEvent(event, city.slug, discoveryBlocked);
  const listeningCaption = discoveryEligible && listeningArtist ? <>
    {t("listenBeforeTickets")} {" "}
    <button type="button" onClick={startPlayer}
      onPointerEnter={() => { void loadJukebox(); }}
      onFocus={() => { void loadJukebox(); }}
      className="cursor-pointer text-acid underline underline-offset-4 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-acid">
      {t("listenToArtists")}
    </button>
  </> : null;
  useDiscoveryWebMcp({
    events: festival ? [] : [event], citySlug: city.slug, blockedSpotifyIds: discoveryBlocked,
    onPreview: (artist) => {
      startPlayback(artist.spotify_id!, artist.name, { event, artistId: artist.id });
    },
    onJukebox: (event, artist) => relatedJukebox.openJukebox({ event, artistId: artist?.id }),
  });

  useEffect(() => {
    if (window.history.length > 1 && document.referrer && document.referrer.startsWith(window.location.origin)) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setHasHistory(true);
    }
  }, []);

  return (
    <AuthProvider initialUser={initialUser}>
        <div className="min-h-screen bg-dark font-sans text-white">
          <div className={`mx-auto max-w-7xl px-4 pt-6 sm:px-6 lg:px-8 ${spotifyBarVisible ? "pb-[220px]" : "pb-6"}`}>
            {/* Header */}
            <div className="mb-6">
              {/* Top bar: breadcrumb + nav */}
              <div className="mb-4 flex items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <Link
                    href="/"
                    className="flex items-center gap-2 transition-opacity hover:opacity-80"
                  >
                    <div className="h-2.5 w-2.5 animate-pulse rounded-full bg-acid shadow-[0_0_10px_#D1F805]" />
                    <span className="font-mono text-xs tracking-[0.2em] text-neutral-500">
                      {tCommon("brand")}
                    </span>
                  </Link>
                  <span className="font-mono text-xs text-neutral-700">/</span>
                  <Link
                    href={`/${city.slug}`}
                    className="font-mono text-xs text-neutral-500 transition-colors hover:text-acid"
                  >
                    {city.name.toUpperCase()}
                  </Link>
                </div>
                <div className="flex items-center gap-3">
                  <SearchButton />
                  <LanguageSwitcher />
                  <UserMenu />
                </div>
              </div>

              {/* Festival banner for child events */}
              {festival && (
                <Link
                  href={`/${festival.citySlug}/event/${festival.slug}`}
                  rel="nofollow" prefetch={false}
                  className="mb-3 flex items-center gap-2 rounded-lg border border-neutral-800 bg-neutral-900/50 px-3 py-2 transition-colors hover:border-acid/30 hover:bg-neutral-900"
                >
                  <Calendar className="h-3.5 w-3.5 shrink-0 text-acid" />
                  <span className="font-mono text-xs text-neutral-400">
                    Part of{" "}
                    <span className="text-acid">{festival.name}</span>
                    {" "}— View Full Lineup
                  </span>
                </Link>
              )}

              {/* Event info — full width, with X to the right */}
              <div className="flex items-start gap-4">
                <div className="min-w-0 flex-1">
                  <h1 className="font-mono text-2xl font-bold uppercase tracking-tight text-white sm:text-3xl">
                    {headline}
                  </h1>
                  <div className="mt-1 flex flex-wrap items-center gap-3">
                    {isCancelled && (
                      <span className="rounded bg-red-600/90 px-2 py-0.5 font-mono text-[10px] font-bold text-white">
                        {t("cancelled")}
                      </span>
                    )}
                    {(genresExpanded ? allGenres : allGenres.slice(0, 3)).map((genre) => (
                      <span
                        key={genre}
                        className="rounded-full border border-acid/30 px-2 py-0.5 font-mono text-[10px] text-acid"
                      >
                        {genre.toUpperCase()}
                      </span>
                    ))}
                    {allGenres.length > 3 && (
                      <button
                        onClick={() => setGenresExpanded(!genresExpanded)}
                        className="rounded-full border border-neutral-700 px-2 py-0.5 font-mono text-[10px] text-neutral-400 hover:text-neutral-300"
                      >
                        {genresExpanded ? tCommon("less") : `+${allGenres.length - 3}`}
                      </button>
                    )}
                    <p className="font-mono text-sm text-neutral-500">
                      {formatDate(event.local_date, locale, t("dateTba"))}
                      {event.local_time && (
                        <span> · {formatTime(event.local_time, locale)}</span>
                      )}
                    </p>
                    <ReportButton
                      entityType="event"
                      entityId={event.id}
                      entityName={event.name}
                    />
                    <AdminEventEditButton event={event} />
                  </div>
                </div>
                {hasHistory && (
                  <button
                    onClick={() => router.back()}
                    className="flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center self-start lg:self-end rounded-full border border-neutral-700/50 text-neutral-500 hover:border-neutral-600 hover:text-white transition-all"
                    aria-label="Go back"
                  >
                    <X className="h-5 w-5" />
                  </button>
                )}
              </div>
            </div>

            {/* Two-column layout: image left, details right */}
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
              {/* Left column: Image */}
              <div>
                {imageUrl && (
                  <figure>
                  <div className="relative w-full overflow-hidden rounded-lg border border-neutral-800">
                    {/* Natural height: the full poster, never cropped to a
                        fixed ratio. The 16:9 hint only reserves space until
                        the photo loads. */}
                    <Image
                      src={imageUrl}
                      alt={event.name}
                      width={1200}
                      height={675}
                      className="block h-auto w-full"
                      sizes="(max-width: 1024px) 100vw, 50vw"
                      priority
                    />
                    {isCancelled && (
                      <div className="absolute top-3 left-3 rounded bg-red-600/90 px-3 py-1 font-mono text-xs font-bold text-white backdrop-blur-sm">
                        {t("cancelled")}
                      </div>
                    )}
                  </div>
                  {listeningCaption && <figcaption className="mt-3 text-xs leading-relaxed text-neutral-400">
                    {listeningCaption}
                  </figcaption>}
                  </figure>
                )}

              </div>

              {/* Right column: Details */}
              <div>
                {/* Event details */}
                <div className="mb-6 space-y-3 rounded-lg border border-neutral-800 bg-surface p-5 pb-4">
                  {/* Venue */}
                  {event.venue.name && (
                  <div className="flex items-start gap-3">
                    <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-neutral-500" />
                    <div className="-mt-0.5">
                      <Link
                        href={`/${city.slug}/${event.venue.slug}`}
                        className="font-mono text-sm text-white transition-colors hover:text-acid"
                      >
                        {event.venue.name}
                      </Link>
                      {event.venue.address && (
                        <p className="mt-0.5 text-xs text-neutral-500">
                          {event.venue.address}
                        </p>
                      )}
                    </div>
                  </div>
                  )}

                  {/* Date & time */}
                  <div className="flex items-center gap-3">
                    <Calendar className="h-4 w-4 shrink-0 text-neutral-500" />
                    <span className="font-mono text-sm text-neutral-300">
                      {formatDate(event.local_date, locale, t("dateTba"))}
                      {event.local_time && (
                        <span className="text-neutral-500"> · {formatTime(event.local_time, locale)}</span>
                      )}
                    </span>
                  </div>

                  {/* Price and tickets */}
                  <div className="flex items-center gap-3">
                    <span className="flex h-4 w-4 shrink-0 items-center justify-center font-mono text-xs text-neutral-500">
                      $
                    </span>
                    <div className="flex flex-wrap items-center gap-x-3">
                      {event.price_min != null || event.price_max != null || !ticketUrl ? (
                        <span className="font-mono text-sm text-neutral-300">
                          {formatPrice(
                            event.price_min,
                            event.price_max,
                            event.price_currency,
                            locale,
                            t("priceTba")
                          )}
                        </span>
                      ) : null}
                      {ticketUrl ? (
                        <a
                          href={ticketUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex min-h-6 items-center gap-2 font-mono text-xs text-acid underline underline-offset-4 transition-colors hover:text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-acid"
                        >
                          {t("getTickets")}
                          <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                        </a>
                      ) : null}
                    </div>
                  </div>
                </div>

                {!imageUrl && listeningCaption && <p className="mb-4 text-xs leading-relaxed text-neutral-400">{listeningCaption}</p>}

                {playerArtist && (
                  <div className="mb-4">
                    <button type="button" onClick={startPlayer}
                      onPointerEnter={() => { void loadJukebox(); }}
                      onPointerDown={() => { void loadJukebox(); }}
                      onFocus={() => { void loadJukebox(); }}
                      className="flex min-h-11 w-full items-center justify-center gap-2 rounded-lg bg-acid px-6 py-3 font-mono text-sm font-bold tracking-wider text-black transition-colors enabled:hover:bg-acid/90 disabled:opacity-40 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-acid">
                      <PlayIcon className="h-4 w-4" aria-hidden="true" />
                      {tJukebox("startPlayer")}
                    </button>
                  </div>
                )}

                {/* Artists */}
                {event.attractions.length > 0 && (
                  <div className="mb-8">
                    <div className="mb-3 flex items-center gap-3">
                      <h2 className="font-mono text-xs font-bold tracking-wider text-neutral-500">
                        {t("artists")}
                      </h2>
                    </div>
                    <div className="divide-y divide-neutral-800/50">
                      {event.attractions.map((a) => (
                        <EventArtistRow
                          key={a.id}
                          attraction={a}
                          blockedSpotifyIds={blockedSpotifyIds}
                          event={event}
                        />
                      ))}
                    </div>
                  </div>
                )}


                {/* Other Showings */}
                {relatedShowings.length > 0 && (
                  <div className="mb-8">
                    <h2 className="mb-3 font-mono text-xs font-bold tracking-wider text-neutral-500">
                      {t("otherShowings")}
                    </h2>
                    <div className="space-y-3">
                      {relatedShowings.map((showing) => (
                        <div
                          key={showing.event_id}
                          className="flex items-center justify-between gap-3 rounded border border-neutral-800 px-4 py-3"
                        >
                          <div className="min-w-0 flex-1">
                            <Link
                              href={`/${city.slug}/event/${showing.event_slug}`}
                              rel="nofollow" prefetch={false}
                              className="font-mono text-xs text-neutral-300 transition-colors hover:text-acid"
                            >
                              {formatDate(showing.local_date, locale, t("dateTba"))}
                              {showing.local_time && (
                                <span className="text-neutral-500">
                                  {" "}
                                  · {formatTime(showing.local_time, locale)}
                                </span>
                              )}
                            </Link>
                            <p className="mt-0.5 font-mono text-[10px] text-neutral-500">
                              {formatPrice(
                                showing.price_min,
                                showing.price_max,
                                showing.price_currency,
                                locale,
                                t("priceTba")
                              )}
                            </p>
                          </div>
                          {showing.event_url ? (
                            <a
                              href={showing.event_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex min-h-11 shrink-0 items-center font-mono text-[10px] text-neutral-400 underline underline-offset-4 transition-colors hover:text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-neutral-400"
                            >
                              {t("getTickets")}
                            </a>
                          ) : (
                            <span className="shrink-0 font-mono text-[10px] text-neutral-600">
                              {t("noLink")}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              </div>
            </div>
          </div>

        </div>

        {relatedJukebox.modalProps && (
          <GenreJukeboxModal
            {...relatedJukebox.modalProps}
            key={relatedJukebox.modalKey}
            genreName={tJukebox("allArtists")}
            selectedGenres={[]}
          />
        )}
    </AuthProvider>
  );
}
